import React from 'react';
import { Card, CardDescription, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, MapPin, Award, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navigation from '@/components/Navigation';

const ProductsPage = () => {
  const products = [
    {
      name: "Ostras Vivas Patagónicas",
      description: "Ostras patagónicas frescas, perfectas para abrir en casa y vivir la experiencia completa del mar.",
      image: "/images/oyster-4263253_1280.jpg",
      origin: "Patagonia",
      badge: "Premium",
      color: "from-ocean-500 to-jade-500"
    },
    {
      name: "Langostinos L1 Premium",
      description: "Langostinos de exportación, congelados a bordo. Máxima calidad y frescura garantizada.",
      image: "/images/seafood-side-dish-1605699_1280.jpg",
      origin: "Mar Argentino",
      badge: "Exportación",
      color: "from-seagreen-500 to-ocean-500"
    },
    {
      name: "Centolla Fueguina",
      description: "Medio kilo de carne de centolla, ya cocida y limpia. Un manjar auténtico del Fin del Mundo.",
      image: "/images/lobster-2154987_1280.jpg",
      origin: "Tierra del Fuego",
      badge: "Exclusivo",
      color: "from-jade-500 to-seagreen-500"
    },
    {
      name: "Pack Experiencia Completa",
      description: "Combinación perfecta de nuestros mejores productos para una experiencia gastronómica única.",
      image: "/images/grilled-scallops-6609305_1280.jpg",
      origin: "Selección Mar de Jade",
      badge: "Bestseller",
      color: "from-deep-400 to-ocean-500"
    }
  ];

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gradient-to-br from-pearl-50/50 via-white to-ocean-25/30">
        {/* Hero Section for Products Page */}
        <motion.div
          className="relative py-20 px-4 sm:px-6 lg:px-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="max-w-7xl mx-auto text-center">
            <motion.h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 font-heading text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <motion.div className="flex items-center justify-center gap-3 mb-4">
                <motion.span
                  className="text-4xl sm:text-5xl lg:text-6xl"
                  animate={{ 
                    rotate: [0, -10, 10, -5, 5, 0],
                    scale: [1, 1.1, 1, 1.05, 1]
                  }}
                  transition={{ 
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  🐙
                </motion.span>
                <span className="text-transparent bg-gradient-to-r from-ocean-600 to-jade-600 bg-clip-text leading-tight">
                  Productos
                </span>
              </motion.div>
              <motion.div 
                className="text-2xl sm:text-3xl lg:text-4xl text-transparent bg-gradient-to-r from-deep-600 to-seagreen-600 bg-clip-text font-light"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.5 }}
              >
                Más Buscados
              </motion.div>
            </motion.h1>
            <motion.p 
              className="text-xl text-deep-600 max-w-3xl mx-auto leading-relaxed mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              Los mejores mariscos y productos del mar de la Patagonia Argentina. 
              Calidad premium directo de nuestras aguas prístinas a tu mesa.
            </motion.p>
          </div>
        </motion.div>

        {/* Products Section - Copied exactly from original */}
        <section className="pb-24 relative overflow-hidden">
          {/* Background with hero-inspired gradient and white film */}
          <div className="absolute inset-0 bg-gradient-to-br from-ocean-50 via-pearl-50 to-jade-50" />
          <div className="absolute inset-0 bg-white/70" />
          
          {/* Floating elements */}
          <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-r from-jade-200/20 to-ocean-200/20 rounded-full blur-xl" />
          <div className="absolute bottom-32 right-16 w-40 h-40 bg-gradient-to-r from-seagreen-200/20 to-jade-200/20 rounded-full blur-xl" />
          <div className="absolute top-1/3 right-1/4 w-24 h-24 bg-gradient-to-r from-ocean-200/30 to-pearl-200/30 rounded-full blur-lg" />

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            
            {/* Products Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 mb-20">
              {products.map((product, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  whileHover={{ y: -10 }}
                  className="group"
                >
                  <Card className="overflow-hidden bg-white/90 backdrop-blur-lg border-0 shadow-xl hover:shadow-2xl transition-all duration-500 rounded-3xl">
                    {/* Image Container */}
                    <div className="relative h-64 md:h-80 overflow-hidden">
                      {/* Product Image */}
                      <motion.img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        whileHover={{ scale: 1.1 }}
                        transition={{ duration: 0.7 }}
                      />
                      
                      {/* Image Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                      
                      {/* Floating Badge */}
                      <motion.div 
                        className="absolute top-6 right-6"
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ duration: 0.8, delay: 0.3 + index * 0.1 }}
                      >
                        <Badge className={`bg-gradient-to-r ${product.color} text-white border-0 shadow-lg backdrop-blur-sm px-4 py-2 text-sm font-semibold`}>
                          {product.badge}
                        </Badge>
                      </motion.div>

                      {/* Origin Tag */}
                      <div className="absolute bottom-4 left-4 flex items-center gap-2">
                        <div className="bg-white/20 backdrop-blur-md rounded-full p-2">
                          <MapPin className="w-4 h-4 text-white" />
                        </div>
                        <span className="text-white font-body text-sm font-medium">
                          {product.origin}
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-8">
                      <CardTitle className="text-2xl md:text-3xl font-title font-bold text-deep-500 mb-4 group-hover:text-ocean-600 transition-colors duration-300">
                        {product.name}
                      </CardTitle>
                      
                      <CardDescription className="text-silver-600 font-body text-lg leading-relaxed mb-6">
                        {product.description}
                      </CardDescription>

                      {/* Features */}
                      <div className="flex flex-wrap gap-3 mb-8">
                        <div className="flex items-center gap-2 bg-gradient-to-r from-ocean-50 to-jade-50 rounded-full px-4 py-2">
                          <Award className="w-4 h-4 text-ocean-600" />
                          <span className="text-ocean-700 font-body text-sm font-medium">Calidad Premium</span>
                        </div>
                        <div className="flex items-center gap-2 bg-gradient-to-r from-seagreen-50 to-ocean-50 rounded-full px-4 py-2">
                          <Clock className="w-4 h-4 text-seagreen-600" />
                          <span className="text-seagreen-700 font-body text-sm font-medium">Fresco del día</span>
                        </div>
                      </div>

                      {/* CTA Button */}
                      <motion.div
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Link to="/precios">
                          <Button className={`w-full bg-gradient-to-r ${product.color} hover:opacity-90 text-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 rounded-2xl py-4 font-body text-lg font-semibold group`}>
                            <span>Ver Precios</span>
                            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                          </Button>
                        </Link>
                      </motion.div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>

            {/* Call to Action Section */}
            <motion.div 
              className="text-center"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <div className="bg-white/90 backdrop-blur-lg rounded-3xl p-8 shadow-2xl border border-white/20">
                <h3 className="text-2xl md:text-3xl font-title font-bold text-deep-500 mb-4">
                  ¿Querés ver todos nuestros productos?
                </h3>
                <p className="text-lg text-silver-600 font-body mb-6 max-w-2xl mx-auto">
                  Descubrí nuestra lista completa de precios con más de 20 productos premium del mar patagónico
                </p>
                
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link to="/precios">
                    <Button 
                      size="lg"
                      className="bg-gradient-to-r from-ocean-500 to-jade-500 hover:from-ocean-600 hover:to-jade-600 text-white border-0 shadow-xl hover:shadow-2xl transition-all duration-300 rounded-2xl px-8 py-4 font-body text-lg"
                    >
                      <span>Ver Lista de Precios Completa</span>
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ProductsPage;
